import React, { useMemo, useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow, parseISO } from 'date-fns';
import api from '../config/api';
import PartnerPlanWall from '../components/PartnerPlanWall';
import PartnerCapacityDialog from '../components/PartnerCapacityDialog';
import {
  PartnerEmptyState,
  PartnerFilterBar,
  PartnerLoading,
  PartnerNote,
  PartnerPageHeader,
  PartnerPageShell,
  partnerDangerLinkSx,
  partnerLinkSx,
  partnerOxbloodBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';
import { canInvitePartnerTeamMember, isPartnerPlanLimitError, partnerCapacityFromError } from '../utils/partnerPlan';

const dialogPaperSx = { bgcolor: DOCUMENT.surface, borderRadius: '2px' };
const dialogTitleSx = { fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: 26, color: DOCUMENT.ink };
const fieldSx = {
  '& .MuiInput-underline:before': { borderBottomColor: DOCUMENT.ruleHard },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInput-underline:after': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInputLabel-root': { color: DOCUMENT.ink3 },
  '& .MuiInputLabel-root.Mui-focused': { color: DOCUMENT.accent },
};
// Fixed last column so header + every row share the same track sizes (auto diverges per row).
const TEAM_GRID_COLUMNS = {
  xs: 'minmax(0, 1fr) 6.75rem',
  md: 'minmax(0, 1.6fr) 6rem 5.5rem minmax(7rem, 1fr) minmax(6rem, 0.9fr) 7.25rem',
};
const teamGridSx = {
  display: 'grid',
  gridTemplateColumns: TEAM_GRID_COLUMNS,
  columnGap: { xs: 1.5, md: 2 },
  rowGap: { xs: 0.75, md: 0 },
  alignItems: 'center',
};

const lastSeenLabel = (user, t) => {
  if (!user?.last_login) return t('partnerTeam.never');
  try {
    return t('partnerProfile.seenWhen', {
      when: formatDistanceToNow(parseISO(String(user.last_login).replace(' ', 'T')), { addSuffix: false }),
    });
  } catch {
    return t('partnerTeam.never');
  }
};

const PartnerTeam = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { entitlements } = usePartnerEntitlements();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteFormData, setInviteFormData] = useState({ name: '', email: '' });
  const [planWallOpen, setPlanWallOpen] = useState(false);
  const [planEntitlements, setPlanEntitlements] = useState(null);
  const [capacityOpen, setCapacityOpen] = useState(false);
  const [capacityError, setCapacityError] = useState(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ is_active: true });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    fetchTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('partnerToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      if (!localStorage.getItem('partnerToken')) {
        navigate('/partner/login');
        return;
      }

      const response = await api.get('/partner-users', { headers: getAuthHeaders() });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError(t('partnerTeam.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteOpen = () => {
    if (canInvitePartnerTeamMember(entitlements) === false) {
      if (entitlements?.limits?.extraSeatCents) {
        setCapacityError({
          code: 'partner_team_limit',
          entitlements,
          checkoutType: 'partner_extra_seat'
        });
        setCapacityOpen(true);
        return;
      }
      setPlanEntitlements(entitlements);
      setPlanWallOpen(true);
      return;
    }

    setInviteFormData({ name: '', email: '' });
    setInviteError('');
    setInviteSuccess('');
    setInviteDialogOpen(true);
  };

  const handleInviteClose = () => {
    setInviteDialogOpen(false);
    setInviteError('');
    setInviteSuccess('');
  };

  const handleInviteChange = (event) => {
    const { name, value } = event.target;
    setInviteFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInviteSubmit = async (event) => {
    event.preventDefault();
    setInviteLoading(true);
    setInviteError('');
    setInviteSuccess('');

    try {
      await api.post('/partner-users/invite', { ...inviteFormData, role: 'member' }, {
        headers: getAuthHeaders(),
      });
      setInviteSuccess(t('partnerTeam.inviteSuccess'));
      setTimeout(() => {
        fetchTeamMembers();
        handleInviteClose();
      }, 1500);
    } catch (err) {
      console.error('Error inviting team member:', err);
      if (isPartnerPlanLimitError(err)) {
        const capacity = partnerCapacityFromError(err);
        setInviteDialogOpen(false);
        if (capacity?.checkoutType === 'partner_extra_seat') {
          setCapacityError(capacity);
          setCapacityOpen(true);
        } else {
          setPlanEntitlements(err.response.data.entitlements || null);
          setPlanWallOpen(true);
        }
      }
      setInviteError(err.response?.data?.error || t('partnerTeam.inviteFailed'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleEditOpen = (user) => {
    setSelectedUser(user);
    setEditFormData({ is_active: Boolean(user.is_active) });
    setEditError('');
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditError('');
    setSelectedUser(null);
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    setEditLoading(true);
    setEditError('');

    try {
      await api.put(`/partner-users/${selectedUser.id}`, editFormData, {
        headers: getAuthHeaders(),
      });
      fetchTeamMembers();
      handleEditClose();
    } catch (err) {
      console.error('Error updating team member:', err);
      setEditError(err.response?.data?.error || t('partnerTeam.updateFailed'));
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteOpen = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/partner-users/${userToDelete.id}`, { headers: getAuthHeaders() });
      fetchTeamMembers();
      handleDeleteClose();
    } catch (err) {
      console.error('Error deleting team member:', err);
      setError(err.response?.data?.error || t('partnerTeam.removeFailed'));
      handleDeleteClose();
    } finally {
      setDeleteLoading(false);
    }
  };

  const canEditUser = (user) => {
    const partner = JSON.parse(localStorage.getItem('partner') || '{}');
    if (user.role === 'owner') return false;
    return user.email !== partner.email;
  };

  const counts = useMemo(() => ({
    all: users.length,
    active: users.filter((user) => user.is_active).length,
    inactive: users.filter((user) => !user.is_active).length,
  }), [users]);

  const visibleUsers = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    return users.filter((user) => {
      if (filter === 'active' && !user.is_active) return false;
      if (filter === 'inactive' && user.is_active) return false;
      if (!needle) return true;
      return [user.name, user.email, user.role, user.invited_by_name]
        .some((value) => String(value || '').toLowerCase().includes(needle));
    });
  }, [filter, searchQuery, users]);

  return (
    <PartnerPageShell error={error} onClearError={() => setError('')}>
      <PartnerPageHeader
        title={t('partnerTeam.title')}
        subtitle={t('partnerTeam.subtitle')}
        actions={(
          <Button sx={partnerOxbloodBtnSx} onClick={handleInviteOpen}>
            {t('partnerTeam.invite')}
          </Button>
        )}
      />

      <PartnerFilterBar
        chips={[
          { id: 'all', label: t('partnerTeam.filterAll'), count: counts.all },
          { id: 'active', label: t('partnerTeam.filterActive'), count: counts.active },
          { id: 'inactive', label: t('partnerTeam.filterInactive'), count: counts.inactive },
        ]}
        activeChip={filter}
        onChipChange={setFilter}
        search={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('partnerTeam.search')}
      />

      {loading ? (
        <PartnerLoading />
      ) : visibleUsers.length === 0 ? (
        <PartnerEmptyState
          title={searchQuery || filter !== 'all' ? t('partnerLists.noResults') : t('partnerTeam.emptyTitle')}
          body={searchQuery || filter !== 'all' ? t('partnerLists.noResultsBody') : t('partnerTeam.emptyBody')}
          action={!searchQuery && filter === 'all' ? (
            <Button sx={partnerOxbloodBtnSx} onClick={handleInviteOpen}>
              {t('partnerTeam.invite')}
            </Button>
          ) : null}
        />
      ) : (
        <Box sx={{ mt: 1, borderTop: `1px solid ${DOCUMENT.rule}` }}>
          <Box
            sx={{
              ...teamGridSx,
              display: { xs: 'none', md: 'grid' },
              py: 1.25,
              borderBottom: `1px solid ${DOCUMENT.rule}`,
            }}
          >
            {[
              { key: 'name', label: t('partnerTeam.name') },
              { key: 'role', label: t('partnerTeam.role') },
              { key: 'status', label: t('partnerTeam.status') },
              { key: 'lastLogin', label: t('partnerTeam.lastLogin') },
              { key: 'invitedBy', label: t('partnerTeam.invitedBy') },
              { key: 'actions', label: '' },
            ].map(({ key, label }) => (
              <Typography key={key} sx={{ fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                {label}
              </Typography>
            ))}
          </Box>
          {visibleUsers.map((user) => {
            const self = !canEditUser(user);
            return (
              <Box
                key={user.id}
                data-testid="partner-team-row"
                sx={{
                  ...teamGridSx,
                  py: 2,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                  bgcolor: DOCUMENT.canvas,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, color: DOCUMENT.ink }}>
                    {user.name}
                    {self && (
                      <Box component="span" sx={{ ml: 1, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                        {t('partnerTeam.you')}
                      </Box>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.35 }}>{user.email}</Typography>
                </Box>
                <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, display: { xs: 'none', md: 'block' } }}>
                  {t(`partnerProfile.roles.${user.role}`, user.role)}
                </Typography>
                <Typography sx={{ fontSize: 13, color: user.is_active ? DOCUMENT.ink2 : DOCUMENT.ink3, display: { xs: 'none', md: 'block' } }}>
                  {user.is_active ? t('partnerTeam.active') : t('partnerTeam.inactive')}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, display: { xs: 'none', md: 'block' } }}>
                  {lastSeenLabel(user, t)}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, display: { xs: 'none', md: 'block' } }}>
                  {user.invited_by_name || t('partnerTeam.dash')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.75, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
                  {self ? (
                    <Typography sx={{ fontSize: 11, color: DOCUMENT.ink3 }}>{t('partnerTeam.dash')}</Typography>
                  ) : (
                    <>
                      <Box component="button" type="button" onClick={() => handleEditOpen(user)} sx={partnerLinkSx}>
                        {t('partnerTeam.edit')}
                      </Box>
                      <Box component="button" type="button" onClick={() => handleDeleteOpen(user)} sx={partnerDangerLinkSx}>
                        {t('partnerTeam.remove')}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <PartnerNote>{t('partnerTeam.note')}</PartnerNote>

      <Dialog
        open={inviteDialogOpen}
        onClose={handleInviteClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
        TransitionProps={{ timeout: 0 }}
      >
        <DialogTitle sx={dialogTitleSx}>{t('partnerTeam.inviteTitle')}</DialogTitle>
        <form onSubmit={handleInviteSubmit}>
          <DialogContent>
            {inviteError && <Alert severity="error" sx={{ mb: 2 }}>{inviteError}</Alert>}
            {inviteSuccess && <Alert severity="success" sx={{ mb: 2 }}>{inviteSuccess}</Alert>}
            <TextField
              fullWidth
              variant="standard"
              label={t('partnerTeam.fullName')}
              name="name"
              value={inviteFormData.name}
              onChange={handleInviteChange}
              required
              disabled={inviteLoading}
              inputProps={{ 'aria-label': t('partnerTeam.fullName') }}
              sx={{ ...fieldSx, mb: 2.5 }}
            />
            <TextField
              fullWidth
              variant="standard"
              label={t('partnerTeam.emailAddress')}
              name="email"
              type="email"
              value={inviteFormData.email}
              onChange={handleInviteChange}
              required
              disabled={inviteLoading}
              inputProps={{ 'aria-label': t('partnerTeam.emailAddress') }}
              sx={fieldSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleInviteClose} disabled={inviteLoading} sx={partnerLinkSx}>
              {t('partnerTeam.cancel')}
            </Button>
            <Button type="submit" disabled={inviteLoading} sx={partnerOxbloodBtnSx}>
              {inviteLoading ? <CircularProgress size={18} sx={{ color: DOCUMENT.ivory }} /> : t('partnerTeam.sendInvite')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
        TransitionProps={{ timeout: 0 }}
      >
        <DialogTitle sx={dialogTitleSx}>{t('partnerTeam.editTitle')}</DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent>
            {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
            <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mb: 3 }}>
              {t('partnerTeam.editing', { name: selectedUser?.name, email: selectedUser?.email })}
            </Typography>
            <TextField
              fullWidth
              select
              variant="standard"
              label={t('partnerTeam.status')}
              value={editFormData.is_active ? 'active' : 'inactive'}
              onChange={(event) => setEditFormData({ is_active: event.target.value === 'active' })}
              required
              disabled={editLoading}
              sx={fieldSx}
            >
              <MenuItem value="active">{t('partnerTeam.active')}</MenuItem>
              <MenuItem value="inactive">{t('partnerTeam.inactive')}</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleEditClose} disabled={editLoading} sx={partnerLinkSx}>
              {t('partnerTeam.cancel')}
            </Button>
            <Button type="submit" disabled={editLoading} sx={partnerOxbloodBtnSx}>
              {editLoading ? <CircularProgress size={18} sx={{ color: DOCUMENT.ivory }} /> : t('partnerTeam.save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        PaperProps={{ sx: dialogPaperSx }}
        TransitionProps={{ timeout: 0 }}
      >
        <DialogTitle sx={dialogTitleSx}>{t('partnerTeam.removeTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14.5, color: DOCUMENT.ink2, lineHeight: 1.65 }}>
            {t('partnerTeam.removeBody', { name: userToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleDeleteClose} disabled={deleteLoading} sx={partnerLinkSx}>
            {t('partnerTeam.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
            sx={{ ...partnerOxbloodBtnSx, background: '#8C2B22', '&:hover': { background: DOCUMENT.oxblood } }}
          >
            {deleteLoading ? <CircularProgress size={18} sx={{ color: DOCUMENT.ivory }} /> : t('partnerTeam.removeConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <PartnerPlanWall
        open={planWallOpen}
        onClose={() => setPlanWallOpen(false)}
        entitlements={planEntitlements}
        reason="team"
      />

      {capacityOpen && (
        <PartnerCapacityDialog
          open={capacityOpen}
          limitError={capacityError}
          onClose={() => setCapacityOpen(false)}
          onSuccess={() => {
            setCapacityOpen(false);
            fetchTeamMembers();
          }}
        />
      )}
    </PartnerPageShell>
  );
};

export default PartnerTeam;
